import React, { useEffect, useRef, useState } from 'react';
import type { PlanLanguage } from '../../api/plan';
import { INTERFACE_TEXT } from '../../constants/interfaceText';
import { fill } from '../../today/todayViewModel';
import {
  languageBadge,
  languageName,
  nativeLanguageName,
} from '../../utils/languageName';
import LanguageChoices from './LanguageChoices';
import styles from './LanguageSwitcher.module.css';

interface LanguageSwitcherProps {
  languages: PlanLanguage[];
  /** Locales that can be added. */
  available: string[];
  /** Make the locale current; rejects when the switch fails. */
  onChoose: (locale: string) => Promise<void>;
}

type MenuView = 'languages' | 'add';

/**
 * The language button at the top left of Today: the current language, and a menu to switch to another one the
 * learner has started or add a new one. Hidden when there is nothing to switch to or add (BR-050).
 */
export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  languages,
  available,
  onChoose,
}) => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<MenuView>('languages');
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const wrap = useRef<HTMLDivElement | null>(null);
  const button = useRef<HTMLButtonElement | null>(null);
  const menu = useRef<HTMLDivElement | null>(null);

  const items = (): HTMLElement[] =>
    Array.from(
      menu.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? []
    );

  // Focus the first row of the view (the current language in the list) whenever the menu or its view opens.
  useEffect(() => {
    if (!open) return;
    const rows = items();
    const current = rows.find(
      (row) => row.getAttribute('aria-checked') === 'true'
    );
    (current ?? rows[0])?.focus();
  }, [open, view]);

  const close = (refocus: boolean): void => {
    setOpen(false);
    setView('languages');
    setFailed(false);
    if (refocus) button.current?.focus();
  };

  const current = languages.find((language) => language.current);
  if (!current || (languages.length <= 1 && available.length === 0)) {
    return null;
  }

  const choose = async (locale: string): Promise<void> => {
    if (pending) return;
    if (locale === current.locale) {
      close(true);
      return;
    }
    setPending(true);
    setFailed(false);
    try {
      await onChoose(locale);
      close(true);
    } catch {
      setFailed(true);
    } finally {
      setPending(false);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (!open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close(true);
      return;
    }
    const rows = items();
    const at = rows.indexOf(document.activeElement as HTMLElement);
    const move = (to: number): void => {
      event.preventDefault();
      rows[(to + rows.length) % rows.length]?.focus();
    };
    if (event.key === 'ArrowDown') move(at + 1);
    else if (event.key === 'ArrowUp') move(at < 0 ? rows.length - 1 : at - 1);
    else if (event.key === 'Home') move(0);
    else if (event.key === 'End') move(rows.length - 1);
  };

  const onBlur = (event: React.FocusEvent): void => {
    if (open && !wrap.current?.contains(event.relatedTarget as Node | null)) {
      close(false);
    }
  };

  return (
    <div
      ref={wrap}
      className={styles.wrap}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
    >
      <button
        ref={button}
        type="button"
        className={styles.button}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={fill(INTERFACE_TEXT.tdLangButton, {
          name: languageName(current.locale),
        })}
        onClick={() => (open ? close(false) : setOpen(true))}
      >
        <span className={styles.code} aria-hidden="true">
          {languageBadge(current.locale)}
        </span>
        <b aria-hidden="true">{languageName(current.locale)}</b>
        <span className={styles.chev} aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div
          ref={menu}
          role="menu"
          aria-label={INTERFACE_TEXT.tdLangMenu}
          className={styles.menu}
        >
          {view === 'languages' ? (
            <>
              {languages.map((language) => {
                const native = nativeLanguageName(language.locale);
                return (
                  <button
                    key={language.locale}
                    type="button"
                    role="menuitemradio"
                    aria-checked={language.current}
                    tabIndex={-1}
                    className={styles.choice}
                    aria-disabled={pending || undefined}
                    onClick={() => void choose(language.locale)}
                  >
                    <span className={styles.code} aria-hidden="true">
                      {languageBadge(language.locale)}
                    </span>
                    <span>
                      {languageName(language.locale)}
                      <small>
                        {native !== languageName(language.locale) && (
                          <>
                            <span lang={language.locale.split(/[-_]/)[0]}>
                              {native}
                            </span>
                            {' · '}
                          </>
                        )}
                        {fill(INTERFACE_TEXT.tdLangPassed, {
                          n: language.passed_count,
                        })}
                      </small>
                    </span>
                    {language.current && (
                      <span className={styles.tick} aria-hidden="true">
                        ✓
                      </span>
                    )}
                  </button>
                );
              })}
              {available.length > 0 && (
                <>
                  <hr className={styles.rule} />
                  <button
                    type="button"
                    role="menuitem"
                    aria-haspopup="menu"
                    tabIndex={-1}
                    className={styles.choice}
                    onClick={() => setView('add')}
                  >
                    <span className={styles.code} aria-hidden="true">
                      +
                    </span>
                    <span>{INTERFACE_TEXT.tdLangAdd}</span>
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                role="menuitem"
                tabIndex={-1}
                className={styles.choice}
                onClick={() => setView('languages')}
              >
                <span>{INTERFACE_TEXT.tdLangBack}</span>
              </button>
              <hr className={styles.rule} />
              <LanguageChoices
                locales={available}
                itemRole="menuitem"
                disabled={pending}
                onChoose={(locale) => void choose(locale)}
              />
            </>
          )}
          {failed && (
            <p role="alert" className={styles.error}>
              {INTERFACE_TEXT.tdLangSwitchFailed}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
